import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Trash2, Edit, AlertCircle, AlertTriangle } from 'lucide-react';
import { useToast } from 'hooks/use-toast';
import { useCreateIntegration } from 'hooks/use-create-integration';
import { useDeleteIntegration } from 'hooks/use-delete-integration';
import { useGetIntegrations } from 'hooks/use-get-integrations';
import { useSyncIntegration } from 'hooks/use-sync-integration';
import { Integration } from 'services/integration-service';
import ftpIcon from 'assets/images/ftp.svg';

/**
 * SFTP Connection Component
 * 
 * Manages SFTP connections with configurable settings.
 * Integrates with the backend API for SFTP integration management.
 * 
 * Features:
 * - Add, edit, view, and delete SFTP configurations
 * - Scope selection (user vs organization)
 * - Professional card-based UI
 * - Backend API integration
 * 
 * @param onBack - Callback to navigate back to connectors list
 * @param existingIntegration - Existing integration if any
 */

interface SFTPConnectionProps {
    onBack: () => void;
    existingIntegration?: Integration;
}

interface ConnectionType {
    id: 'user' | 'organization';
    label: string;
    selected: boolean;
}

interface SFTPFormData {
    displayName: string;
    host: string;
    port: number;
    username: string;
    password: string;
}

interface ConfigDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (config: SFTPFormData) => void;
    existingIntegration?: Integration | null;
    isLoading: boolean;
}

const RadioButton = ({ selected }: { selected: boolean }) => (
    <div className="relative size-5 flex items-center justify-center">
        <div className={`w-5 h-5 rounded-full border-2 ${selected ? 'border-primary' : 'border-neutral-300'}`}>
            {selected && (
                <div className="w-full h-full rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-primary" />
                </div>
            )}
        </div>
    </div>
);

const SFTPConfigDialog = ({ isOpen, onClose, onSave, existingIntegration, isLoading }: ConfigDialogProps) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<SFTPFormData>({
        displayName: '',
        host: '',
        port: 22,
        username: '',
        password: ''
    });

    useEffect(() => {
        if (existingIntegration) {
            const connectionData = existingIntegration.integration_type_metadata || {};
            setFormData({
                displayName: existingIntegration.display_name || '',
                host: connectionData.host || '',
                port: connectionData.port || 22,
                username: connectionData.username || '',
                password: '' // Don't populate password for security
            });
        } else {
            setFormData({
                displayName: '',
                host: '',
                port: 22,
                username: '',
                password: ''
            });
        }
    }, [existingIntegration, isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-background border-b border-border px-6 py-4">
                    <h2 className="text-xl font-semibold text-foreground">
                        {existingIntegration ? t('EDIT_SFTP_CONFIGURATION') || 'Edit SFTP Configuration' : t('ADD_SFTP_CONFIGURATION') || 'Add SFTP Configuration'}
                    </h2>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Display Name */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            {t('DISPLAY_NAME') || 'Display Name'} <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.displayName}
                            onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="SFTP Server"
                            required
                        />
                    </div>

                    {/* Host */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            {t('HOST') || 'Host'} <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.host}
                            onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="eg: 10.10.6.15"
                            required
                        />
                    </div>

                    {/* Port */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            {t('PORT') || 'Port'} <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="number"
                            value={formData.port}
                            onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) || 22 })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="eg: 22"
                            required
                        />
                    </div>

                    {/* Username */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            {t('USERNAME') || 'Username'} <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="text"
                            value={formData.username}
                            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder=""
                            required
                        />
                    </div>

                    {/* Password */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-foreground">
                            {t('PASSWORD') || 'Password'} <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder=""
                            required
                        />
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-border">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isLoading}
                        >
                            {t('CANCEL') || 'Cancel'}
                        </Button>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="bg-primary hover:bg-primary-600"
                        >
                            {isLoading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                    {t('SAVING') || 'Saving...'}
                                </>
                            ) : (
                                t('SAVE') || 'Save'
                            )}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export const SFTPConnection = ({ onBack, existingIntegration }: SFTPConnectionProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

    const { mutate: createIntegration, isPending: isCreating } = useCreateIntegration();
    const { mutate: deleteIntegration, isPending: isDeleting } = useDeleteIntegration();
    const { mutate: syncIntegration, isPending: isSyncing } = useSyncIntegration();
    const { refetch: refetchIntegrations } = useGetIntegrations();

    const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>(() => {
        const existingScope = existingIntegration?.scope as 'user' | 'organization' | undefined;
        return [
            { id: 'user', label: 'CONNECT_FOR_INDIVIDUAL_ACCOUNT', selected: existingScope === 'user' || !existingScope },
            { id: 'organization', label: 'CONNECT_FOR_ORGANIZATION_ACCOUNT', selected: existingScope === 'organization' }
        ];
    });

    // Update connection types when existingIntegration changes
    useEffect(() => {
        if (existingIntegration) {
            const existingScope = existingIntegration.scope as 'user' | 'organization';
            setConnectionTypes(prev =>
                prev.map(type => ({ ...type, selected: type.id === existingScope }))
            );
        }
    }, [existingIntegration]);

    const handleConnectionTypeSelect = (id: 'user' | 'organization') => {
        setConnectionTypes(prev =>
            prev.map(type => ({ ...type, selected: type.id === id }))
        );
    };

    const handleAddConfiguration = () => {
        setIsDialogOpen(true);
    };

    const handleEditConfiguration = () => {
        setIsDialogOpen(true);
    };

    const handleSaveConfiguration = (formData: SFTPFormData) => {
        const selectedScope = connectionTypes.find(type => type.selected)?.id || 'user';

        // If editing, delete the old integration first, then create a new one
        if (existingIntegration) {
            deleteIntegration({
                connectionId: existingIntegration.connection_id,
                integrationType: 'sftp'
            }, {
                onSuccess: () => {
                    // After deleting, create new integration with updated data
                    createNewIntegration(formData, selectedScope);
                },
                onError: (error: any) => {
                    console.error('Failed to delete existing integration:', error);
                    toast({
                        variant: 'destructive',
                        title: t('ERROR') || 'Error',
                        description: t('FAILED_TO_UPDATE_CONFIGURATION') || 'Failed to update configuration'
                    });
                }
            });
        } else {
            // Create new integration
            createNewIntegration(formData, selectedScope);
        }
    };

    const createNewIntegration = (formData: SFTPFormData, scope: string) => {
        const payload = {
            scope: scope,
            integration_type: 'sftp',
            display_name: formData.displayName,
            connection_data: {
                host: formData.host,
                port: formData.port,
                username: formData.username,
                password: formData.password,
                private_key: null
            }
        };

        // eslint-disable-next-line no-console
        console.log('Creating SFTP integration with payload:', payload);

        createIntegration(payload as any, {
            onSuccess: () => {
                toast({
                    variant: 'success',
                    title: t('SUCCESS') || 'Success',
                    description: existingIntegration
                        ? t('CONFIGURATION_UPDATED_SUCCESSFULLY') || 'Configuration updated successfully'
                        : t('CONFIGURATION_CREATED_SUCCESSFULLY') || 'Configuration created successfully'
                });
                setIsDialogOpen(false);
                refetchIntegrations();
            },
            onError: (error: any) => {
                console.error('Failed to create SFTP integration:', error);
                toast({
                    variant: 'destructive',
                    title: t('ERROR') || 'Error',
                    description: error?.message || t('FAILED_TO_SAVE_CONFIGURATION') || 'Failed to save configuration'
                });
            }
        });
    };

    const handleDeleteClick = () => {
        setIsDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (existingIntegration) {
            deleteIntegration({
                connectionId: existingIntegration.connection_id,
                integrationType: 'sftp'
            }, {
                onSuccess: () => {
                    toast({
                        variant: 'success',
                        title: t('SUCCESS') || 'Success',
                        description: t('CONFIGURATION_DELETED_SUCCESSFULLY') || 'Configuration deleted successfully'
                    });
                    setIsDeleteConfirmOpen(false);
                    refetchIntegrations();
                },
                onError: (error: any) => {
                    console.error('Failed to delete SFTP integration:', error);
                    toast({
                        variant: 'destructive',
                        title: t('ERROR') || 'Error',
                        description: error?.message || t('FAILED_TO_DELETE_CONFIGURATION') || 'Failed to delete configuration'
                    });
                }
            });
        }
    };

    const handleConnect = () => {
        if (existingIntegration) {
            syncIntegration(existingIntegration.connection_id, {
                onSuccess: () => {
                    toast({
                        variant: 'success',
                        title: t('SUCCESS') || 'Success',
                        description: t('SFTP_CONNECTED_SUCCESSFULLY') || 'SFTP connected successfully'
                    });
                    refetchIntegrations();
                },
                onError: (error: any) => {
                    console.error('Failed to connect SFTP:', error);
                    toast({
                        variant: 'destructive',
                        title: t('ERROR') || 'Error',
                        description: error?.message || t('FAILED_TO_CONNECT_SFTP') || 'Failed to connect SFTP'
                    });
                }
            });
        }
    };

    const handleDisconnect = () => {
        if (existingIntegration) {
            // For disconnect, we DON'T pass integrationType
            // This means it won't use hard_delete=true
            // The backend will soft-delete (set is_active to false) instead of removing the record
            deleteIntegration({
                connectionId: existingIntegration.connection_id,
            }, {
                onSuccess: async () => {
                    toast({
                        variant: 'success',
                        title: t('SUCCESS') || 'Success',
                        description: t('SFTP_DISCONNECTED_SUCCESSFULLY') || 'SFTP disconnected successfully'
                    });
                    // Refetch to get the updated integration with is_active: false
                    await refetchIntegrations();
                },
                onError: (error: any) => {
                    console.error('Failed to disconnect SFTP:', error);
                    toast({
                        variant: 'destructive',
                        title: t('ERROR') || 'Error',
                        description: error?.message || t('FAILED_TO_DISCONNECT_SFTP') || 'Failed to disconnect SFTP'
                    });
                }
            });
        }
    };

    // Extract connection data from existing integration
    const connectionData = existingIntegration?.integration_type_metadata || {};

    return (
        <div className="h-full overflow-y-auto bg-background">
            <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 space-y-6 sm:space-y-8 max-w-4xl">
                {/* Back Button */}
                <button
                    onClick={onBack}
                    className="flex items-center gap-[5px] text-primary hover:text-primary/80 transition-colors"
                >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="text-[14px] font-medium leading-[22px]">{t('BACK') || 'back'}</span>
                </button>

                {/* SFTP Header with Edit/Delete buttons */}
                <div className="pb-4 border-b border-light-gray">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-1">
                            <div className="w-[48px] h-[48px] sm:w-[52px] sm:h-[52px] border border-light-gray rounded-lg flex items-center justify-center p-3">
                                <img src={ftpIcon} alt="SFTP" className="w-full h-full object-contain" />
                            </div>
                            <div className="space-y-1 flex-1">
                                <h3 className="text-[15px] sm:text-[16px] font-bold text-foreground leading-[1.4]">
                                    SFTP
                                </h3>
                                <p className="text-[11px] sm:text-[12px] text-foreground leading-[1.4] tracking-[-0.24px]">
                                    {t('CONNECT_TO_SFTP_SERVER') || 'Connect to SFTP server'}
                                </p>
                            </div>
                        </div>

                        {/* Edit and Delete Icon buttons (only show if integration exists) */}
                        {existingIntegration && (
                            <div className="flex gap-2 flex-shrink-0">
                                <button
                                    onClick={handleEditConfiguration}
                                    className="p-2 hover:bg-muted rounded-md transition-colors"
                                    title={t('EDIT_CONFIGURATION') || 'Edit Configuration'}
                                >
                                    <Edit className="h-5 w-5 text-foreground" />
                                </button>
                                <button
                                    onClick={handleDeleteClick}
                                    className="p-2 hover:bg-destructive/10 rounded-md transition-colors"
                                    title={t('DELETE_CONFIGURATION') || 'Delete Configuration'}
                                >
                                    <Trash2 className="h-5 w-5 text-destructive" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Empty State - Show when no integration exists */}
                {!existingIntegration && (
                    <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-border rounded-lg bg-muted/20">
                        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
                            <img src={ftpIcon} alt="SFTP" className="w-8 h-8 object-contain" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            {t('NO_CONFIGURATIONS') || 'No Configurations'}
                        </h3>
                        <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
                            {t('NO_SFTP_CONFIGURATIONS_MESSAGE') || 'You haven\'t added any SFTP configurations yet. Click the button below to get started.'}
                        </p>
                        <Button
                            onClick={handleAddConfiguration}
                            className="flex items-center gap-2 bg-primary hover:bg-primary-600"
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            {t('ADD_FIRST_CONFIGURATION') || 'Add First Configuration'}
                        </Button>
                    </div>
                )}

                {/* Show configuration sections only when integration exists */}
                {existingIntegration && (
                    <>
                        {/* Connection Type Selection (Scope) */}
                        <div className="space-y-3">
                            <h4 className="text-[13px] font-medium text-foreground leading-[1.25]">
                                {t('SELECT_CONNECTION_TYPE') || 'Select connection type'}
                            </h4>
                            <div className="flex flex-col sm:flex-row gap-3">
                                {connectionTypes.map((type) => (
                                    <button
                                        key={type.id}
                                        onClick={() => handleConnectionTypeSelect(type.id)}
                                        className="flex-1 sm:max-w-[230px] p-3 sm:p-4 border border-light-gray rounded-lg flex items-center gap-2 hover:border-border transition-colors"
                                    >
                                        <RadioButton selected={type.selected} />
                                        <span className="text-[11px] sm:text-[12px] text-foreground leading-[1.4] tracking-[-0.24px] text-left">
                                            {t(type.label)}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {/* Connection Type Warnings */}
                            {connectionTypes.find(type => type.id === 'user')?.selected && (
                                <div className="flex items-start gap-2 p-3 sm:p-4 bg-[hsl(var(--warning-background))] border border-[hsl(var(--warning))] rounded-lg">
                                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary-500))] flex-shrink-0 mt-0.5" />
                                    <p className="text-[11px] sm:text-[12px] text-[hsl(var(--warning-high-emphasis))] leading-[1.6] tracking-[-0.24px]">
                                        {t('ONLY_YOU_WILL_HAVE_ACCESS_TO_THE_CONNECTED_FILES') || 'Only you will have access to your connected files. They will not be shared with your organization'}
                                    </p>
                                </div>
                            )}

                            {connectionTypes.find(type => type.id === 'organization')?.selected && (
                                <div className="flex items-start gap-2 p-3 sm:p-4 bg-[hsl(var(--warning-background))] border border-[hsl(var(--warning))] rounded-lg">
                                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary-500))] flex-shrink-0 mt-0.5" />
                                    <p className="text-[11px] sm:text-[12px] text-[hsl(var(--warning-high-emphasis))] leading-[1.6] tracking-[-0.24px]">
                                        {t('AVAILABLE_TO_ALL_ORGANIZATION_MEMBERS') || 'Available to all organization members'}
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Configuration Information Card */}
                        <div className="space-y-3">
                            <h4 className="text-[15px] font-semibold text-foreground">
                                {t('INFORMATION') || 'Information'}
                            </h4>
                            <div className="bg-card border border-border rounded-lg p-4">
                                <div className="space-y-2.5">
                                    {/* Display Name */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {t('DISPLAY_NAME') || 'Display Name'}
                                        </span>
                                        <span className="text-sm text-foreground font-medium">
                                            {existingIntegration.display_name}
                                        </span>
                                    </div>

                                    {/* Host */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {t('HOST') || 'Host'}
                                        </span>
                                        <span className="text-sm text-foreground font-medium">
                                            {connectionData.host || '-'}
                                        </span>
                                    </div>

                                    {/* Port */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {t('PORT') || 'Port'}
                                        </span>
                                        <span className="text-sm text-foreground font-medium">
                                            {connectionData.port || '-'}
                                        </span>
                                    </div>

                                    {/* Username */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {t('USERNAME') || 'Username'}
                                        </span>
                                        <span className="text-sm text-foreground font-medium">
                                            {connectionData.username || '-'}
                                        </span>
                                    </div>

                                    {/* Status */}
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-muted-foreground">
                                            {t('STATUS') || 'Status'}
                                        </span>
                                        <span className={`text-sm font-medium ${existingIntegration.is_active ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground'}`}>
                                            {existingIntegration.is_active ? t('ACTIVE') || 'Active' : t('INACTIVE') || 'Inactive'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer with Connect/Disconnect Button */}
                        <div className="pt-4 border-t border-light-gray flex justify-center sm:justify-end">
                            {existingIntegration.is_active ? (
                                <Button
                                    onClick={handleDisconnect}
                                    disabled={isDeleting}
                                    className="min-w-[120px] bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                                >
                                    {isDeleting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                            {t('DISCONNECTING') || 'Disconnecting...'}
                                        </>
                                    ) : (
                                        t('DISCONNECT') || 'Disconnect'
                                    )}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleConnect}
                                    disabled={isSyncing}
                                    className="min-w-[120px] bg-primary hover:bg-primary-600"
                                >
                                    {isSyncing ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                            {t('CONNECTING') || 'Connecting...'}
                                        </>
                                    ) : (
                                        t('CONNECT') || 'Connect'
                                    )}
                                </Button>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Add/Edit Configuration Dialog */}
            <SFTPConfigDialog
                isOpen={isDialogOpen}
                onClose={() => {
                    setIsDialogOpen(false);
                }}
                onSave={handleSaveConfiguration}
                existingIntegration={existingIntegration}
                isLoading={isCreating || isDeleting}
            />

            {/* Delete Confirmation Dialog */}
            {isDeleteConfirmOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-background rounded-lg shadow-lg max-w-md w-full p-6">
                        <div className="flex items-start gap-4 mb-4">
                            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                                <AlertCircle className="h-5 w-5 text-destructive" />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {t('DELETE_CONFIGURATION') || 'Delete Configuration'}
                                </h3>
                                <p className="text-sm text-muted-foreground">
                                    {t('DELETE_CONFIGURATION_CONFIRMATION') || 'Are you sure you want to delete this configuration? This action cannot be undone.'}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsDeleteConfirmOpen(false);
                                }}
                                disabled={isDeleting}
                            >
                                {t('CANCEL') || 'Cancel'}
                            </Button>
                            <Button
                                onClick={handleDeleteConfirm}
                                disabled={isDeleting}
                                className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                            >
                                {isDeleting ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                        {t('DELETING') || 'Deleting...'}
                                    </>
                                ) : (
                                    t('DELETE') || 'Delete'
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
